import { motion, AnimatePresence } from 'framer-motion'
import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar, Search, Filter, Play,
  Clock, Users, Star, TrendingUp, ArrowRight,
  Sparkles, MapPin, Compass
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import toast from 'react-hot-toast'
import {
  mockEvents,
  getUpcomingEvents,
  getFeaturedEvents,
  getEventsByType,
  EventType
} from '../../components/events/mockEventData'
import { CalendarView } from '../../components/events/CalendarView'
import { cn } from '@/lib/utils'

type ViewMode = 'discover' | 'calendar'
type FilterType = 'all' | 'demo' | 'webinar' | 'workshop' | 'tour' | 'launch'

export default function EventsCalendar() {
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useState<ViewMode>('discover')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [showPastEvents, setShowPastEvents] = useState(false)

  // Filter and search events - separate upcoming and past
  const { upcomingFilteredEvents, pastFilteredEvents } = useMemo(() => {
    let events = mockEvents

    if (filterType !== 'all') {
      events = getEventsByType(filterType)
    }

    if (searchTerm) {
      events = events.filter((event: EventType) =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.presenter.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    const now = new Date()
    // Event is "past" only AFTER it ends (endTime), not when it starts
    const upcoming = events.filter((event: EventType) => event.endTime > now)
      .sort((a: EventType, b: EventType) => a.startTime.getTime() - b.startTime.getTime())

    const past = events.filter((event: EventType) => event.endTime <= now)
      .sort((a: EventType, b: EventType) => b.endTime.getTime() - a.endTime.getTime()) // Most recent first

    return { upcomingFilteredEvents: upcoming, pastFilteredEvents: past }
  }, [searchTerm, filterType])

  // Legacy support for components expecting filteredEvents
  const filteredEvents = useMemo(() => {
    return showPastEvents
      ? [...upcomingFilteredEvents, ...pastFilteredEvents]
      : upcomingFilteredEvents
  }, [upcomingFilteredEvents, pastFilteredEvents, showPastEvents])

  // Memoized expensive computations
  const upcomingEvents = useMemo(() => getUpcomingEvents(6), [])
  const featuredEvents = useMemo(() => getFeaturedEvents(), [])

  // Get the next upcoming event (closest to now, ignoring filters)
  const nextEvent = useMemo(() => {
    const now = new Date()
    const allUpcoming = mockEvents
      .filter((event: EventType) => event.endTime > now)
      .sort((a: EventType, b: EventType) => a.startTime.getTime() - b.startTime.getTime())
    return allUpcoming[0] || null
  }, [])

  // Memoized categories array
  const categories = useMemo(() => [
    { name: 'Live Demos', type: 'demo', count: getEventsByType('demo').length, gradient: 'from-blue-500 to-cyan-500', icon: '🖥️' },
    { name: 'Webinars', type: 'webinar', count: getEventsByType('webinar').length, gradient: 'from-purple-500 to-pink-500', icon: '🌐' },
    { name: 'Workshops', type: 'workshop', count: getEventsByType('workshop').length, gradient: 'from-orange-500 to-red-500', icon: '🔧' },
    { name: 'Lab Tours', type: 'tour', count: getEventsByType('tour').length, gradient: 'from-green-500 to-teal-500', icon: '🏢' },
    { name: 'Launches', type: 'launch', count: getEventsByType('launch').length, gradient: 'from-yellow-400 to-orange-500', icon: '🚀' }
  ], [])

  // Memoized stats array
  const stats = useMemo(() => [
    { label: 'Live Events', value: upcomingEvents.length, icon: Clock },
    { label: 'Total Attendees', value: '2.4K+', icon: Users },
    { label: 'Featured', value: featuredEvents.length, icon: Star },
    { label: 'This Month', value: mockEvents.length, icon: TrendingUp }
  ], [upcomingEvents.length, featuredEvents.length])

  // Memoized event handler
  const handleEventClick = useCallback((eventSlug: string) => {
    navigate(`/events/${eventSlug}`)
  }, [navigate])

  // Memoized scroll handlers
  const handleScrollToCategories = useCallback(() => {
    setViewMode('discover')
    requestAnimationFrame(() => {
      setTimeout(() => {
        const element = document.getElementById('categories-section')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 400)
    })
  }, [])

  const handleScrollToCalendar = useCallback(() => {
    setViewMode('calendar')
    requestAnimationFrame(() => {
      setTimeout(() => {
        const element = document.getElementById('calendar-view')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 400)
    })
  }, [])

  // Custom toast notifications for mobile buttons
  const showMobileToast = useCallback((icon: React.ReactNode, title: string, description: string, gradient: string) => {
    toast.custom((t) => (
      <motion.div
        initial={{ opacity: 0, y: -50, scale: 0.9 }}
        animate={{ opacity: t.visible ? 1 : 0, y: t.visible ? 0 : -50, scale: t.visible ? 1 : 0.9 }}
        exit={{ opacity: 0, y: -50, scale: 0.9 }}
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${gradient} p-1 shadow-2xl max-w-md`}
      >
        <div className="relative overflow-hidden rounded-xl bg-white dark:bg-gray-900 p-4">
          <div className="flex items-start gap-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br ${gradient} shadow-lg`}>
              <div className="text-white">
                {icon}
              </div>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 dark:text-white text-sm">
                {title}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                {description}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    ), {
      duration: 2000,
      position: 'top-center',
    })
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-950 dark:via-blue-950/30 dark:to-purple-950/30">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl animate-pulse will-change-transform" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse will-change-transform" style={{ animationDelay: '2s' }} />
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl animate-pulse will-change-transform" style={{ animationDelay: '4s' }} />
        </div>

        <div className="relative pt-20 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Main Hero */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-full px-6 py-2 mb-8 shadow-lg"
              >
                <Sparkles className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Discover Amazing Events
                </span>
              </motion.div>

              {/* Main Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl lg:text-7xl font-bold mb-6 tracking-tight"
              >
                <span className="bg-gradient-to-r from-gray-900 via-blue-800 to-purple-800 dark:from-white dark:via-blue-200 dark:to-purple-200 bg-clip-text text-transparent">
                  Events That
                </span>
                <br />
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Inspire
                </span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8 leading-relaxed"
              >
                Join live demos, educational webinars, and exclusive workshops.
                Connect with industry experts and discover cutting-edge technology.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
              >
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-shadow group"
                  onClick={handleScrollToCategories}
                >
                  <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Explore Events
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 px-8 py-3 rounded-full transition-colors transition-border group"
                  onClick={handleScrollToCalendar}
                >
                  <Calendar className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  View Calendar
                </Button>
              </motion.div>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-2xl p-6 text-center border border-gray-200/30 dark:border-gray-700/30 shadow-lg hover:shadow-xl transition-shadow group cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="sticky top-16 z-40 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4">
          {/* Mobile Layout */}
          <div className="flex sm:hidden flex-col gap-3 py-4">
            <div className="flex items-center gap-3 flex-1">
              {/* View Toggle & Past Events - Icon Only Buttons */}
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-full p-1 shrink-0 gap-1">
                {/* Discover */}
                <button
                  className={cn(
                    "p-2.5 rounded-full transition-colors transition-shadow",
                    viewMode === 'discover'
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  )}
                  onClick={() => {
                    setViewMode('discover')
                    showMobileToast(
                      <Compass className="w-5 h-5" />,
                      'Discover Mode',
                      'Browse featured events and categories',
                      'from-blue-500 to-cyan-500'
                    )
                  }}
                  title="Discover Events"
                >
                  <Compass className="w-4 h-4" />
                </button>

                {/* Calendar */}
                <button
                  className={cn(
                    "p-2.5 rounded-full transition-colors transition-shadow",
                    viewMode === 'calendar'
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  )}
                  onClick={() => {
                    setViewMode('calendar')
                    showMobileToast(
                      <Calendar className="w-5 h-5" />,
                      'Calendar View',
                      'View events in calendar format',
                      'from-indigo-500 to-purple-500'
                    )
                  }}
                  title="Calendar View"
                >
                  <Calendar className="w-4 h-4" />
                </button>

                {/* Past Events Toggle */}
                <button
                  className={cn(
                    "p-2.5 rounded-full transition-colors transition-shadow",
                    showPastEvents
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  )}
                  onClick={() => {
                    setShowPastEvents(!showPastEvents)
                    showMobileToast(
                      <Clock className="w-5 h-5" />,
                      showPastEvents ? 'Upcoming Only' : 'All Events',
                      showPastEvents ? 'Showing only upcoming events' : 'Showing past and upcoming events',
                      'from-purple-500 to-pink-500'
                    )
                  }}
                  title={showPastEvents ? "Hide Past Events" : "Show Past Events"}
                >
                  <Clock className="w-4 h-4" />
                </button>
              </div>

              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white/70 dark:bg-gray-800/70 border-gray-200/50 dark:border-gray-700/50 rounded-full w-full"
                />
              </div>
            </div>

            {/* Active Filter Indicator */}
            {filterType !== 'all' && (
              <div className="flex items-center gap-2 self-end">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-800"
                >
                  <Filter className="w-3.5 h-3.5" />
                  <span className="capitalize">{filterType}</span>
                  <button
                    onClick={() => setFilterType('all')}
                    className="ml-1 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-full p-0.5 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </motion.div>
              </div>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:block relative py-4">
            <div className="flex items-center">
              {/* Left: View Toggle - Pulled left */}
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-full p-1 -ml-4">
                <button
                  className={cn(
                    "px-6 py-2 rounded-full text-sm font-medium transition-colors transition-shadow whitespace-nowrap",
                    viewMode === 'discover'
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  )}
                  onClick={() => setViewMode('discover')}
                >
                  Discover
                </button>
                <button
                  className={cn(
                    "px-6 py-2 rounded-full text-sm font-medium transition-colors transition-shadow whitespace-nowrap",
                    viewMode === 'calendar'
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                  )}
                  onClick={() => setViewMode('calendar')}
                >
                  Calendar
                </button>
              </div>

              {/* Center: Search - Absolutely centered */}
              <div className="absolute left-1/2 -translate-x-1/2 w-full max-w-md px-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-white/70 dark:bg-gray-800/70 border-gray-200/50 dark:border-gray-700/50 rounded-full w-full"
                  />
                </div>
              </div>

              {/* Right: Event Stats & Filter */}
              <div className="ml-auto flex items-center gap-3">
                {/* Event Counter */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full border border-blue-100 dark:border-blue-800/50"
                >
                  <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 dark:text-gray-400 leading-none">Events</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white leading-none mt-0.5">
                      {filteredEvents.length}
                    </span>
                  </div>
                </motion.div>

                {/* Active Filter Badge */}
                {filterType !== 'all' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 dark:bg-blue-600 text-white rounded-full text-sm font-medium shadow-lg shadow-blue-500/30 whitespace-nowrap"
                  >
                    <Filter className="w-3.5 h-3.5" />
                    <span className="capitalize">{filterType}</span>
                    <button
                      onClick={() => setFilterType('all')}
                      className="ml-1 hover:bg-white/20 rounded-full p-0.5 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </motion.div>
                )}

                {/* Show Past Events Toggle */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowPastEvents(!showPastEvents)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-full border transition-all",
                    showPastEvents
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 border-purple-400 shadow-lg shadow-purple-500/30"
                      : "bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-700/50 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                  )}
                  title={showPastEvents ? "Hide past events" : "Show past events"}
                >
                  <Clock className={cn("w-4 h-4", showPastEvents ? "text-white" : "text-purple-600 dark:text-purple-400")} />
                  <div className="flex flex-col">
                    <span className={cn("text-xs leading-none", showPastEvents ? "text-purple-100" : "text-gray-500 dark:text-gray-400")}>
                      {showPastEvents ? "Showing" : "Timeline"}
                    </span>
                    <span className={cn("text-sm font-bold leading-none mt-0.5", showPastEvents ? "text-white" : "text-gray-900 dark:text-white")}>
                      {showPastEvents ? "All Events" : "Upcoming"}
                    </span>
                  </div>
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            {viewMode === 'discover' && (
              <motion.div
                key="discover"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-12"
              >
                {/* Categories */}
                <div id="categories-section">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mb-6"
                  >
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                      Browse by Category
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Filter events by type
                    </p>
                  </motion.div>

                  <div className="flex flex-wrap gap-3">
                    {/* All Events Button */}
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        setFilterType('all')
                        setTimeout(() => {
                          const element = document.getElementById('upcoming-events')
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                        }, 100)
                      }}
                      className={cn(
                        "group relative px-5 py-2.5 rounded-full text-sm font-medium overflow-hidden",
                        filterType === 'all'
                          ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-lg"
                          : "bg-white/60 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-800/80 border border-gray-200/50 dark:border-gray-700/50"
                      )}
                    >
                      {/* Glass morphism hover effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 backdrop-blur-sm" />
                      <span className="relative z-10 flex items-center gap-2">
                        All Events
                        {filterType === 'all' && (
                          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-1.5 h-1.5 bg-current rounded-full" />
                        )}
                      </span>
                    </motion.button>

                    {/* Category Buttons - Only show categories with events */}
                    {categories.filter(cat => cat.count > 0).map((category, index) => (
                      <motion.button
                        key={category.name}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                          setFilterType(category.type as EventType['type'])
                          setTimeout(() => {
                            const element = document.getElementById('upcoming-events')
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                            }
                          }, 100)
                        }}
                        className={cn(
                          "group relative px-5 py-2.5 rounded-full text-sm font-medium overflow-hidden",
                          filterType === category.type
                            ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-lg"
                            : "bg-white/60 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-800/80 border border-gray-200/50 dark:border-gray-700/50"
                        )}
                      >
                        {/* Glass morphism hover effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 backdrop-blur-sm" />
                        <span className="relative z-10 flex items-center gap-2">
                          {category.name}
                          <span className={cn(
                            "text-xs",
                            filterType === category.type ? "opacity-80" : "opacity-60"
                          )}>
                            {category.count}
                          </span>
                          {filterType === category.type && (
                            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-1.5 h-1.5 bg-current rounded-full" />
                          )}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Next Event Highlight */}
                {nextEvent && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="relative"
                  >
                    {/* Gradient Border Container */}
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-1 shadow-2xl">
                      {/* Inner Content */}
                      <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-gray-900">
                        {/* Animated Background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-purple-50/50 to-pink-50/50 dark:from-blue-950/30 dark:via-purple-950/30 dark:to-pink-950/30" />
                        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] dark:opacity-[0.05]" />

                        <div className="relative grid lg:grid-cols-5 gap-4 lg:gap-8 p-4 sm:p-6 lg:p-12">
                          {/* Left: Event Image - Takes 2 columns */}
                          <div className="lg:col-span-2">
                            <div className="relative h-full min-h-[200px] sm:min-h-[280px] lg:min-h-[320px] rounded-2xl overflow-hidden shadow-2xl group">
                              {/* Event Image */}
                              <img
                                src={nextEvent.imageUrl || '/placeholder-event.jpg'}
                                alt={nextEvent.title}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                              />

                              {/* Gradient Overlay */}
                              <div
                                className="absolute inset-0 bg-gradient-to-br opacity-40 group-hover:opacity-30 transition-opacity"
                                style={{
                                  background: `linear-gradient(135deg, ${nextEvent.gradientFrom}, ${nextEvent.gradientTo})`
                                }}
                              />

                              {/* Animated Pulse Ring */}
                              <div className="absolute top-6 left-6">
                                <div className="relative">
                                  <div className="absolute w-3 h-3 bg-red-500 rounded-full animate-ping" />
                                  <div className="relative w-3 h-3 bg-red-500 rounded-full" />
                                </div>
                              </div>

                              {/* "NEXT EVENT" Badge */}
                              <div className="absolute top-4 left-12 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md px-5 py-2.5 rounded-full shadow-2xl border border-white/20">
                                <span className="text-sm font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent tracking-wider">
                                  NEXT EVENT
                                </span>
                              </div>

                              {/* Type Badge */}
                              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
                                <span className={cn(
                                  "px-4 py-2 rounded-full text-xs font-bold backdrop-blur-md border shadow-lg",
                                  nextEvent.type === 'demo' && "bg-blue-500/90 text-white border-blue-400/50",
                                  nextEvent.type === 'webinar' && "bg-purple-500/90 text-white border-purple-400/50",
                                  nextEvent.type === 'workshop' && "bg-orange-500/90 text-white border-orange-400/50",
                                  nextEvent.type === 'tour' && "bg-green-500/90 text-white border-green-400/50",
                                  nextEvent.type === 'launch' && "bg-yellow-500/90 text-white border-yellow-400/50"
                                )}>
                                  {nextEvent.type.toUpperCase()}
                                </span>
                                {nextEvent.isFeatured && (
                                  <span className="px-3 py-2 rounded-full text-xs font-bold bg-yellow-400/95 text-yellow-900 border border-yellow-300/50 backdrop-blur-md shadow-lg flex items-center gap-1.5">
                                    <Star className="w-3.5 h-3.5 fill-current" />
                                    Featured
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Right: Event Details - Takes 3 columns */}
                          <div className="lg:col-span-3 flex flex-col justify-between gap-4 lg:gap-6">
                            {/* Event Info */}
                            <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                              <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
                                {nextEvent.title}
                              </h2>

                              <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-300 leading-relaxed line-clamp-2 lg:line-clamp-3">
                                {nextEvent.description}
                              </p>

                              {/* Event Meta */}
                              <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-1 sm:pt-2">
                                <div className="flex items-center gap-2 sm:gap-3 text-gray-700 dark:text-gray-300">
                                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 dark:bg-blue-900/30">
                                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
                                  </div>
                                  <div>
                                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium">Date</p>
                                    <p className="text-xs sm:text-sm font-semibold">
                                      {nextEvent.startTime.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 sm:gap-3 text-gray-700 dark:text-gray-300">
                                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 dark:bg-purple-900/30">
                                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
                                  </div>
                                  <div>
                                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium">Time</p>
                                    <p className="text-xs sm:text-sm font-semibold">
                                      {nextEvent.startTime.toLocaleTimeString('en-US', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 sm:gap-3 text-gray-700 dark:text-gray-300">
                                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-pink-100 dark:bg-pink-900/30">
                                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-pink-600 dark:text-pink-400" />
                                  </div>
                                  <div>
                                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium">Location</p>
                                    <p className="text-xs sm:text-sm font-semibold">{nextEvent.location}</p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 sm:gap-3 text-gray-700 dark:text-gray-300">
                                  <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 dark:bg-green-900/30">
                                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
                                  </div>
                                  <div>
                                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium">Attendees</p>
                                    <p className="text-xs sm:text-sm font-semibold">{nextEvent.maxAttendees} max</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Countdown Timer */}
                            <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                              <p className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Starts In
                              </p>
                              <div className="flex gap-2 sm:gap-3">
                                {(() => {
                                  const now = new Date()
                                  const diff = nextEvent.startTime.getTime() - now.getTime()
                                  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
                                  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
                                  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

                                  return (
                                    <>
                                      <div className="flex-1 text-center p-2 sm:p-3 lg:p-4 rounded-xl lg:rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                                        <div className="text-xl sm:text-2xl lg:text-4xl font-bold text-white">{days}</div>
                                        <div className="text-[10px] sm:text-xs font-medium text-blue-100 mt-0.5 sm:mt-1 uppercase tracking-wide">Days</div>
                                      </div>
                                      <div className="flex-1 text-center p-2 sm:p-3 lg:p-4 rounded-xl lg:rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
                                        <div className="text-xl sm:text-2xl lg:text-4xl font-bold text-white">{hours}</div>
                                        <div className="text-[10px] sm:text-xs font-medium text-purple-100 mt-0.5 sm:mt-1 uppercase tracking-wide">Hours</div>
                                      </div>
                                      <div className="flex-1 text-center p-2 sm:p-3 lg:p-4 rounded-xl lg:rounded-2xl bg-gradient-to-br from-pink-500 to-pink-600 shadow-lg">
                                        <div className="text-xl sm:text-2xl lg:text-4xl font-bold text-white">{minutes}</div>
                                        <div className="text-[10px] sm:text-xs font-medium text-pink-100 mt-0.5 sm:mt-1 uppercase tracking-wide">Minutes</div>
                                      </div>
                                    </>
                                  )
                                })()}
                              </div>

                              {/* CTA Button */}
                              <Button
                                onClick={() => handleEventClick(nextEvent.slug)}
                                className="w-full h-10 sm:h-11 lg:h-12 text-sm sm:text-base font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl"
                              >
                                View Event Details
                                <ArrowRight className="w-5 h-5 ml-2" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Featured Events */}
                {featuredEvents.length > 0 && (
                  <div>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between mb-6"
                    >
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Featured Events
                      </h2>
                      <Button
                        variant="ghost"
                        className="text-blue-600 hover:text-blue-700"
                        onClick={() => {
                          setFilterType('all')
                          setSearchTerm('')
                          // Smooth scroll to upcoming events section
                          setTimeout(() => {
                            const element = document.getElementById('upcoming-events')
                            if (element) {
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' })
                            }
                          }, 100)
                        }}
                      >
                        View All
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {featuredEvents.slice(0, 3).map((event: EventType, index: number) => (
                        <motion.div
                          key={event.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -4 }}
                          className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow group cursor-pointer border border-gray-100 dark:border-gray-700"
                          onClick={() => handleEventClick(event.slug)}
                        >
                          {/* Event Image */}
                          <div className="relative h-48 overflow-hidden">
                            {/* Background Image */}
                            {event.imageUrl ? (
                              <img
                                src={event.imageUrl}
                                alt={event.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600" />
                            )}

                            {/* Gradient Overlay */}
                            <div
                              className="absolute inset-0"
                              style={{
                                background: `linear-gradient(135deg, ${event.gradientFrom}88, ${event.gradientTo}88)`
                              }}
                            />

                            {/* Featured Badge */}
                            <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 z-10">
                              <Star className="w-3 h-3" />
                              Featured
                            </div>

                            {/* Play overlay */}
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <Play className="w-6 h-6 text-white ml-1" />
                              </div>
                            </div>
                          </div>

                          {/* Event Content */}
                          <div className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                                {event.type.toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(event.startTime).toLocaleDateString()}
                              </span>
                            </div>

                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {event.title}
                            </h3>

                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                              {event.description}
                            </p>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {event.currentAttendees}/{event.maxAttendees}
                                </div>
                              </div>

                              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transform group-hover:translate-x-1 transition-colors transition-transform" />
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Events */}
                <div id="upcoming-events">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between mb-6"
                  >
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Upcoming Events
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {upcomingFilteredEvents.length} event{upcomingFilteredEvents.length !== 1 ? 's' : ''} scheduled
                      </p>
                    </div>
                  </motion.div>

                  {upcomingFilteredEvents.length === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                      <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400">No upcoming events found</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {upcomingFilteredEvents.map((event: EventType, index: number) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ y: -4 }}
                        className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group cursor-pointer border border-gray-100 dark:border-gray-700"
                        onClick={() => handleEventClick(event.slug)}
                      >
                        {/* Event Image */}
                        {event.imageUrl && (
                          <div className="relative h-40 overflow-hidden">
                            <img
                              src={event.imageUrl}
                              alt={event.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                            {/* Gradient Overlay */}
                            <div
                              className="absolute inset-0"
                              style={{
                                background: `linear-gradient(135deg, ${event.gradientFrom}66, ${event.gradientTo}66)`
                              }}
                            />
                            {/* Featured Badge */}
                            {event.isFeatured && (
                              <div className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 z-10">
                                <Star className="w-3 h-3 fill-current" />
                                Featured
                              </div>
                            )}
                          </div>
                        )}

                        <div className="p-6">
                          <div className="flex items-center gap-2 mb-3">
                            <span className={cn(
                              "text-xs font-medium px-2 py-1 rounded-full",
                              event.type === 'demo' && "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30",
                              event.type === 'webinar' && "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30",
                              event.type === 'workshop' && "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30",
                              event.type === 'tour' && "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30",
                              event.type === 'launch' && "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30"
                            )}>
                              {event.type.toUpperCase()}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(event.startTime).toLocaleDateString()}
                            </span>
                          </div>

                          <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {event.title}
                          </h3>

                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                            {event.description}
                          </p>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {event.currentAttendees}/{event.maxAttendees}
                              </div>
                            </div>

                            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transform group-hover:translate-x-1 transition-colors transition-transform" />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  )}
                </div>

                {/* Past Events Section - Only show when toggle is ON */}
                {showPastEvents && pastFilteredEvents.length > 0 && (
                  <div id="past-events" className="mt-16">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center justify-between mb-6"
                    >
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                          Past Events
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {pastFilteredEvents.length} past event{pastFilteredEvents.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {pastFilteredEvents.map((event: EventType, index: number) => (
                        <motion.div
                          key={event.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ y: -4 }}
                          className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group cursor-pointer border border-gray-100 dark:border-gray-700 opacity-75"
                          onClick={() => handleEventClick(event.slug)}
                        >
                          {/* Event Image */}
                          {event.imageUrl && (
                            <div className="relative h-40 overflow-hidden">
                              <img
                                src={event.imageUrl}
                                alt={event.title}
                                className="w-full h-full object-cover grayscale"
                                loading="lazy"
                                decoding="async"
                              />
                              {/* Gradient Overlay - Darker for past events */}
                              <div
                                className="absolute inset-0 bg-gray-900/40"
                              />
                              {/* Past Event Badge */}
                              <div className="absolute top-3 left-3 bg-gray-600 text-white px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 z-10">
                                <Clock className="w-3 h-3" />
                                Past Event
                              </div>
                            </div>
                          )}

                          <div className="p-6">
                            <div className="flex items-center gap-2 mb-3">
                              <span className={cn(
                                "text-xs font-medium px-2 py-1 rounded-full",
                                event.type === 'demo' && "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30",
                                event.type === 'webinar' && "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30",
                                event.type === 'workshop' && "text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30",
                                event.type === 'tour' && "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30",
                                event.type === 'launch' && "text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30"
                              )}>
                                {event.type.toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(event.startTime).toLocaleDateString()}
                              </span>
                            </div>

                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {event.title}
                            </h3>

                            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                              {event.description}
                            </p>

                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {event.currentAttendees}/{event.maxAttendees}
                                </div>
                              </div>

                              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transform group-hover:translate-x-1 transition-colors transition-transform" />
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {viewMode === 'calendar' && (
              <motion.div
                key="calendar"
                id="calendar-view"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <CalendarView
                  events={filteredEvents}
                  onEventClick={(event) => handleEventClick(event.slug)}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </div>
  )
}
